import { QueryClient, useQuery } from 'react-query';
import { PatientClinic } from '../contexts/PatientPortalContext';
import healthcomService from '../services/healthcom.service';

export const ME_CLINICS_QUERY_KEY = ['meClinics'] as const;

const ME_CLINICS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

type MeClinicsResponse = {
  clinics: PatientClinic[];
};

const fetchMeClinics = (): Promise<MeClinicsResponse> => healthcomService.meClinics();

/**
 * React-Query-cached version of `/me/clinics`.
 *
 * Both RootLanding (clinic picker on first login) and Container
 * (post-navigation `Switch Clinic` menu) need this list. Before caching,
 * landing on `/:path` with an active Cognito session fired the endpoint
 * twice in quick succession. With a 5-minute staleTime the second caller
 * gets the cached response instantly.
 */
export const useMeClinics = (enabled = true) =>
  useQuery<MeClinicsResponse>(ME_CLINICS_QUERY_KEY, fetchMeClinics, {
    enabled,
    staleTime: ME_CLINICS_STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });

/**
 * Imperative variant for callers that need the data inside an effect but
 * still want to share the cache with `useMeClinics`. Returns the cached
 * response immediately if fresh, otherwise fires the request and caches
 * the result.
 */
export const fetchMeClinicsCached = (queryClient: QueryClient) =>
  queryClient.fetchQuery(ME_CLINICS_QUERY_KEY, fetchMeClinics, {
    staleTime: ME_CLINICS_STALE_TIME,
  });
